import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useMemo } from 'react'

type FetchFromServerHook = {
  fetchFromServer: (path: string, init?: RequestInit) => Promise<Response>
  fetchJsonFromServer: <T>(path: string, init?: RequestInit) => Promise<T>
}
export const useFetchFromServer = (): FetchFromServerHook => {
  const { getAccessTokenSilently } = useAuth0()

  const appendAuthorizationHeader = useCallback(
    async (currentHeaders: HeadersInit | undefined) => {
      const token = await getAccessTokenSilently()
      const headers = new Headers(currentHeaders)
      headers.append('Authorization', `Bearer ${token}`)

      return headers
    },
    [getAccessTokenSilently],
  )

  const fetchFromServer = useCallback(
    async (path: string, options?: RequestInit): Promise<Response> => {
      const headers = await appendAuthorizationHeader(options?.headers)
      const response = await fetch(path, { ...options, headers })
      if (!response.ok) {
        throw new Error(
          `fetch request to "${path}" failed with status "${response.statusText}" (${response.status}) and message: '${(await response.json()).message}'`
        )
      }
      return response
    },
    [appendAuthorizationHeader],
  )

  const fetchJsonFromServer = useCallback(
    (path: string, init?: RequestInit) =>
      fetchFromServer(path, init).then((r) => r.json()),
    [fetchFromServer],
  )

  return useMemo(
    () => ({
      fetchFromServer,
      fetchJsonFromServer,
    }),
    [fetchFromServer, fetchJsonFromServer],
  )
}
